import mitt from 'mitt';

class EventBus {
    constructor(vueInstance) {
        const event_bus = mitt();
        // Store a map of original handlers to an array of their {type, wrappedHandler} pairs
        const handlerWrappers = new Map();

        this.$on = (type, originalHandler) => {
            // Create a new wrapper function for each $on call
            const wrappedHandler = args => {
                if (Array.isArray(args)) {
                    originalHandler(...args);
                } else {
                    originalHandler(args);
                }
            };

            if (!handlerWrappers.has(originalHandler)) {
                handlerWrappers.set(originalHandler, []);
            }
            // Store the type and the specific wrapper instance
            handlerWrappers.get(originalHandler).push({ type, wrappedHandler });
            event_bus.on(type, wrappedHandler);
        };

        this.$off = (type, originalHandler) => {
            if (originalHandler) {
                // Remove a specific listener
                const wrappers = handlerWrappers.get(originalHandler);
                if (wrappers) {
                    for (let i = wrappers.length - 1; i >= 0; i--) {
                        if (wrappers[i].type === type) {
                            // Remove the specific wrappedHandler that was registered
                            event_bus.off(type, wrappers[i].wrappedHandler);
                            // Remove the stored reference
                            wrappers.splice(i, 1);
                            break; 
                        }
                    }
                    if (wrappers.length === 0) {
                        handlerWrappers.delete(originalHandler);
                    }
                }
            } else {
                // Remove all listeners for the given type if originalHandler is not provided
                event_bus.off(type);

                // Clean up our handlerWrappers map
                for (const [handlerKey, wrappersArray] of handlerWrappers.entries()) {
                    const remainingWrappers = wrappersArray.filter(wrapper => wrapper.type !== type);
                    if (remainingWrappers.length === 0) {
                        handlerWrappers.delete(handlerKey);
                    } else if (remainingWrappers.length < wrappersArray.length) {
                        // Only update if the array actually changed for this key
                        handlerWrappers.set(handlerKey, remainingWrappers);
                    }
                }
            }
        };

        this.$emit = (type, ...args) => {
            // send the arguments as an array to the handler set by $on
            event_bus.emit(type, args);
        };

        if (vueInstance.version && vueInstance.version.startsWith('2.')) {
            // Vue 2 integration
            vueInstance.prototype.$event_bus = this;
        } else if (vueInstance.config && vueInstance.config.globalProperties) {
            // Vue 3 integration
            vueInstance.config.globalProperties.$event_bus = this;
            // Also provide it for inject at app level
            vueInstance.provide('eventBus', this);
        }

        window.$event_bus = this;
    }
}

export default EventBus;